import { useState, useEffect, Component, lazy, Suspense } from 'react';
import { useAuth, useToast } from './hooks';
import { AppContext, useSharedData } from './AppContext';
import ModuleLoader from './components/ModuleLoader';
import Login from './components/Login';
import Layout from './components/Layout';
import Home from './components/Home';
import './index.css';
import './layout.css';
const OrdersModule = lazy(() => import('./components/OrdersModule'));
const AdminModule = lazy(() => import('./components/AdminModule'));
const ProductionModule = lazy(() => import('./components/ProductionModule'));
const AttendanceModule = lazy(() => import('./components/AttendanceModule'));
const SalaryModule = lazy(() => import('./components/SalaryModule'));
const TasksModule = lazy(() => import('./components/TasksModule'));
const CratesModule = lazy(() => import('./components/CratesModule'));
const RecipeModule = lazy(() => import('./components/RecipeModule'));
const AssetsOpsModule = lazy(() => import('./components/AssetsOpsModule'));

// Default first tab for each module
const DEFAULT_TABS = {
  attendance: 'mark',
  salary:     'payroll',
  recipes:    'recipes',
  admin:      'team',
  production: 'hotsnacks',
  assets:     'assets',
  crates:     'today',
  tasks:      'tasks',
};

// ── Error Boundary ───────────────────────────────────────────────────────────
// Catches runtime errors in any module and shows a recovery UI
// instead of a blank white screen
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // In production you could send this to an error reporting service
    // e.g. Sentry, LogRocket etc.
    void error; void info;
  }

  reset() {
    this.setState({ error: null });
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '60vh', padding: 32,
          textAlign: 'center',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: 'rgba(229,62,62,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16,
          }}>
            <i className="ti ti-alert-triangle"
              style={{ fontSize: 26, color: 'var(--lyt-red)' }}/>
          </div>
          <div style={{
            fontSize: 'calc(var(--fs) + 4px)', fontWeight: 700,
            color: 'var(--lyt-t1)', marginBottom: 8,
          }}>
            Something went wrong
          </div>
          <div style={{
            fontSize: 'var(--fs)', color: 'var(--lyt-t4)',
            marginBottom: 24, maxWidth: 320,
          }}>
            This module ran into an unexpected error.
            Your data is safe — tap below to go back.
          </div>
          <button
            onClick={() => this.reset()}
            style={{
              padding: '10px 24px', borderRadius: 10,
              background: 'var(--lyt-accent)', color: '#fff',
              border: 'none', fontFamily: 'Inter', fontSize: 'var(--fs)',
              fontWeight: 600, cursor: 'pointer',
            }}>
            Go Back
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const { user, profile, loading } = useAuth();
  const { toast, show: showToast }  = useToast();
  const sharedData = useSharedData(user);
  const [module, setModule]         = useState(null);
  const [moduleTab, setModuleTab]   = useState(null);
  const [salaryStaffId, setSalaryStaffId] = useState(null);
  const [transitioning, setTransitioning] = useState(false);

  const canAccessModule = (m) => {
    if (!m) return true;
    if (!profile) return false;
    if (profile.role === 'admin') return true;
    const adminOnly = ['admin', 'salary'];
    if (m === 'assets') return profile.role==='admin' || (profile.modules&&(profile.modules.includes('assets')||profile.modules.includes('assets_log')));
    if (adminOnly.includes(m)) return false;
    const allowed = profile.modules;
    if (!allowed) return true;
    return allowed.includes(m);
  };

  const goToModule = (m) => {
    if (m && !canAccessModule(m)) return;
    setTransitioning(true);
    setTimeout(() => {
      setModule(m);
      setModuleTab(DEFAULT_TABS[m] || null);
      setTransitioning(false);
      if (m) window.history.pushState({ module: m }, '');
    }, 80);
  };

  useEffect(() => {
    window.history.pushState({}, '');
    const onPop = () => {
      setModule(prev => {
        if (prev) { window.history.pushState({}, ''); return null; }
        return prev;
      });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  if (loading) return (
    <div className="loading-screen">
      <div className="spinner"/>
      <div style={{ fontSize:'calc(var(--fs) - 1px)', color:'var(--t2)' }}>Loading…</div>
    </div>
  );

  if (!user) return <Login/>;

  const renderContent = () => {
    if (module && !canAccessModule(module)) {
      return <Home onModule={goToModule} onOpenNotes={()=>{setModuleTab('notes');goToModule('tasks');}}/>;
    }
    switch (module) {
      case 'orders':
        return <OrdersModule tab={moduleTab}/>;
      case 'production':
        return <ProductionModule tab={moduleTab}/>;
      case 'attendance':
        return <AttendanceModule tab={moduleTab}
          onOpenPayslip={profile?.role==='admin'?(id)=>{setSalaryStaffId(id);goToModule('salary');}:null}/>;
      case 'tasks':
        return <TasksModule tab={moduleTab} defaultTab={moduleTab}/>;
      case 'salary':
        return <SalaryModule tab={moduleTab} focusStaffId={salaryStaffId} onClearFocus={()=>setSalaryStaffId(null)}/>;
      case 'admin':
        return <AdminModule tab={moduleTab}/>;
      case 'crates':
        return <CratesModule tab={moduleTab}/>;
      case 'recipes':
        return <RecipeModule tab={moduleTab}/>;
      case 'assets':
        return <AssetsOpsModule tab={moduleTab}/>;
      default:
        return <Home onModule={goToModule} onOpenNotes={()=>{setModuleTab('notes');goToModule('tasks');}}/>;
    }
  };

  // All modules use Layout shell
  return (
    <AppContext.Provider value={{ user, profile, showToast, ...sharedData }}>
    <>
      <Layout
        module={module}
        onModule={goToModule}
        tab={moduleTab}
        onTabChange={setModuleTab}
      >
        <ErrorBoundary key={module||'home'}>
          <Suspense fallback={<ModuleLoader/>}>
            <div className={transitioning ? 'page-exit' : 'page-enter'}>
              {renderContent()}
            </div>
          </Suspense>
        </ErrorBoundary>
      </Layout>
      {toast && <div className="toast" style={{bottom:24}}>{toast}</div>}
    </>
    </AppContext.Provider>
  );
}
